<template>

<app-content :loading="is.loading">

	<app-content-head :title="title" :back="$route.meta.back" :readonly="$authorised('con/schedule/access', 'read')">

		<app-content-head-actions-item :disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />
		<app-content-head-actions-item :loading="is.saving && !is.accepting" :disabled="!isDirty || !isValid || is.accepting" icon="save" text="Save" v-on:click="onSaveClick" />
		<app-content-head-actions-item v-if="!isNew && (isLive || isCancelled)" :disabled="isNew" :danger="true" icon="delete" text="Delete" v-on:click="onDeleteClick" />

	</app-content-head>

	<app-content-body v-if="!is.loading">

		<app-content-summary v-if="!isNew">

			<app-content-summary-item label="Status" :tag="statusText" :status="statusTheme" :theme="statusTheme" />
			<app-content-summary-item v-if="hasGame && item.badge" label="Badge" :value="item.badge.name" :link="linkBadge" />
			<app-content-summary-item label="Location" :grow="true" :value="item.location" />
			<app-content-summary-item label="Time" :grow="true" :value="item.date | formatDate('dddd, h:mma')" />
			<app-content-summary-item v-if="isLive" :label="(model.ticket) ? 'Tickets available' : 'Seats available'" :value="item.seats.available + ' / ' + item.seats.total" />
			<app-content-summary-item v-if="!isLive" :label="(model.ticket) ? 'Tickets' : 'Seats'" :value="item.seats.total" />
			<app-content-summary-item label="Created On" :value="item.created | localDate('MM/DD/YYYY h:mma')" />

		</app-content-summary>

		<app-content-box>

			<app-input-select v-if="isNew && $_.keys(references.rooms).length" label="Room" placeholder="Select room" v-model="model.room" :validation="$v.model.room" :options="$util.options(references.rooms, 'name', 'id')" />
			<app-input-suggest v-if="isNew" label="Host" placeholder="Search badges..." v-model="model.host.key" api="convention/badge" :validation="$v.model.host.key" />
			<app-input-scheduletime v-on:picker="is.picking = $event" :locked="isCancelled" :has-picker="hasSpaces" :room="room" :spaces="spaces" :show-space="hasSpaces" label="Schedule" v-model="model" :is-custom="true" v-on:space="model.space = $event" v-on:day="model.day = $event" v-on:time="model.time = $event" v-on:duration="model.duration = $event" />

		</app-content-box>

		<app-content-box :tabs="contentTabs" :forceTab="forceTab" :readonly="$authorised('con/schedule/access', 'read') || isCancelled">

			<template v-slot:about>

				<app-input-suggest v-if="hasGame" v-on:textchange="onGameNameChange" label="Game" placeholder="Select game" image="image" v-model="model.game" api="bgg/game" :validation="$v.model.game" />
				<app-input-text label="Name" placeholder="Enter name..." v-model="model.name" :validation="$v.model.name" />
				<app-input-text label="Description" :autogrow="true" placeholder="Enter description..." v-model="model.description" :validation="$v.model.description" />
				<app-input-image  label="Image" v-model="model.image" :validation="$v.model.image" />
				
			</template>

			<template v-slot:labels>

				<app-input-select v-if="$_.keys(references.types).length" label="Type" placeholder="Select type" v-model="model.type" :validation="$v.model.type" :options="references.types" />
				<app-input-toggle label="Min. Age" :slim="true" v-model="model.age" :validation="$v.model.age" :options="ageOptions" />
				<app-input-toggle v-if="hasGame" label="Min. Experience" :slim="true" v-model="model.experience" :validation="$v.model.experience" :options="expOptions" :tooltips="expTooltips" />
				<app-input-toggle label="Teacher Wanted" v-model="model.teacher" :validation="$v.model.teacher" v-if="hasGame" />
				<app-input-checklist v-if="$_.keys(references.lobbies).length" label="Lobbies" v-model="model.lobbies" :validation="$v.model.lobbies" :options="references.lobbies" />
				
			</template>

			<template v-slot:seats>

				<app-input-toggle v-if="hasGame" label="Host playing" :options="hostOptions" v-model="model.seats.host" :validation="$v.model.seats.host" />
				<app-input-text :label="(model.ticket) ? 'Total' : 'Total seats'" placeholder="Enter number..." v-model="model.seats.total" :validation="$v.model.seats.total" />
				<app-input-text v-if="hasGame" label="Blocked seats" placeholder="Enter number..." v-model="model.seats.blocked" :validation="$v.model.seats.blocked" />

			</template>

			<template v-slot:time>

				<app-input-text label="Location" placeholder="Enter directions..." v-model="model.location.directions" :validation="$v.model.location.directions" />
				
			</template>

			<template v-slot:admin>

				<app-input-text label="Custom Host" maxlength="32" :toggle="model.host.enabled" v-on:toggle="model.host.enabled = !model.host.enabled" notes="Customise the name of the host to something other than the user that created the event." placeholder="Enter name..." v-model="model.host.name" :validation="$v.model.host.name" />
				<app-input-toggle label="Official" v-model="model.official" :validation="$v.model.official" />
				<app-input-toggle label="Enable Clashing" v-model="model.clashes.enabled" :validation="$v.model.clashes.enabled" />
				<app-input-select label="Ticketed" v-model="model.ticket" :allow-deselect="true" placeholder="Not ticketed" :validation="$v.model.ticket" :options="references.tickets" />

			</template>

		</app-content-box>

		<app-widget-chat v-if="!isNew" :no-head="true" :id="item.id" :can-delete="true" />
		<app-widget-activity v-if="!isNew" :no-head="true" :id="item.id" />

	</app-content-body>

	<app-spaces v-if="is.picking" v-on:close="is.picking = false" :is-picking="is.picking" :room="room" :spaces="spaces" :scheduled="scheduled" />

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'
import { required } from 'vuelidate/lib/validators'

export default {

	mixins: [mixForm],

	data: function() {

		return {
			is: {
				picking: false
			},
			forceTab: false,
			readonly: this.$authorised('con/schedule/access', 'read'),
			references: {
				tickets: [],
				rooms: [],
				spaces: [],
				types: [],
				lobbies: [],
				scheduled: []
			},
			hostOptions: {
				0: 'Yes',
				1: 'No'
			},
			locationTypeOptions: {
				0: 'Official table',
				1: 'Other space'
			},
			model: {
				id: false,
				official: 0,
				cancelled: 0,
				name: '',
				ticket: 0,
				teacher: 0,
				game: 0,
				badge: '',
				room: '',
				space: '',
				type: 0,
				lobbies: [],
				experience: 0,
				age: 0,
				description: '',
				image: '',
				clashes: {
					enabled: 1
				},
				host: {
					enabled: 0,
					name: '',
					key: 0
				},
				seats: {
					host: 0,
					total: 0,
					blocked: 0
				},
				day: 0,
				time: 0,
				duration: 0,
				location: {
					skip: 0,
					room: 0,
					space: 0,
					custom: 0,
					directions: ''
				}
			}
		}

	},

	validations: {
		model: {
			name: {
				required
			},
			host: {},
			seats: {},
			location: {},
			clashes: {}
		}
	},

	computed: {

		contentTabs: function() {

			var tabs = [
				{ name: 'about', text: 'About' },
				{ name: 'labels', text: 'Labels' },
				{ name: 'seats', text: (this.model.ticket) ? 'Tickets' : 'Seats' }
			];

			if (!this.hasSpaces) tabs.push({ name: 'time', text: 'Location' })

			tabs.push({ name: 'admin', text: 'Settings' })

			return tabs

		},

		room: function() {

			return (this.model.room) ? this.$_.findWhere(this.references.rooms, {
				id: this.model.room
			}) : false

		},

		hasSpaces: function() {

			return (this.room) ? this.room.spaces : false
		
		},

		isLive: function() {
		
			return this.item.status === this.$constants.schedule.event.status.live
		
		},

		isCancelled: function() {
		
			return this.item.cancelled
		
		},

		spaces: function() {

			if(!this.room) return []

			var spaces = []

			this.$_.each(this.references.spaces, function(space) {
			
				if (space.room.id === this.room.id) spaces.push(space)
			
			}.bind(this))

			return spaces

		},

		scheduled: function() {

			if(!this.room) return []

			var scheduled = []

			this.$_.each(this.references.scheduled, function(event) {
			
				if (event.room === this.room.id) scheduled.push(event)
			
			}.bind(this))

			return scheduled

		},

		statusText: function() {

			if (this.item.cancelled) {
			
				return 'Cancelled'

			} else if(this.item.status === this.$constants.schedule.event.status.live) {
			
				return 'Scheduled'

			} else if(this.item.status === this.$constants.schedule.event.status.proposal) {
			
				return 'Proposed'

			}

			return false
		
		},

		statusTheme: function() {
		
			if (this.item.cancelled) {
			
				return 'red'

			} else if(this.item.status === this.$constants.schedule.event.status.live) {
			
				return 'green'

			} else if(this.item.status === this.$constants.schedule.event.status.proposal) {
			
				return 'yellow'

			} 

			return false

		},

		selectedType: function() {

			return (this.model.type) ? this.$_.findWhere(this.references.types, {
				id: this.model.type
			}) : false

		},

		hasGame: function() {

			return (this.selectedType) ? this.selectedType.games.enabled : true

		},

		ageOptions: function() {

			return this.$constants.schedule.ageShort

		},

		linkBadge: function() {
		
			return (this.item.badge) ? {
				name: 'Convention.Badges.Holders.Edit',
				params: {
					id: this.item.badge.id
				}
			} : false
		
		},

		expOptions: function() {

			return this.$constants.schedule.experienceShort

		},

		expTooltips: function() {

			return this.$constants.schedule.experienceTooltip

		}

	},

	methods: {

		onGameNameChange: function(name) {

			this.model.name = name

		}

	}

}

</script>

<style scoped>

</style>